import { ConversationCard } from "@app/components/assistant/conversation/cards/ConversationCard";
import type { GmailSendMailInput } from "@app/lib/api/actions/servers/gmail/types";
import { Button, GmailLogo, Send01, Trash01 } from "@dust-tt/sparkle";

interface GmailSendMailSummaryCardProps {
  inputs: GmailSendMailInput;
  isValidating: boolean;
  isPulsing: boolean;
  errorMessage: string | null;
  onSend: () => void;
  onDiscard: () => void;
  onReview?: () => void;
}

function toPreviewText(body: string): string {
  return body
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h\d)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function GmailSendMailSummaryCard({
  inputs,
  isValidating,
  isPulsing,
  errorMessage,
  onSend,
  onDiscard,
  onReview,
}: GmailSendMailSummaryCardProps) {
  const isReply = Boolean(inputs.replyToMessageId);
  const recipients = inputs.to ?? [];
  const recipientLine =
    recipients.length > 0
      ? `To ${recipients.join(", ")}`
      : "To the people on the thread";

  return (
    <ConversationCard
      visual={GmailLogo}
      status={{ label: "Waiting for you", tone: "attention" }}
      eyebrow={isReply ? "Gmail reply" : "Gmail"}
      title={inputs.subject ?? "Reply on the thread"}
      meta={recipientLine}
      onExpand={onReview}
      expandLabel="Review and edit in side panel"
      headerActions={
        <Button
          variant="ghost"
          size="xs"
          icon={Trash01}
          tooltip="Discard"
          aria-label="Discard"
          disabled={isValidating}
          onClick={onDiscard}
        />
      }
      footerStart={
        errorMessage ? (
          <span className="text-xs font-medium text-warning-800">
            {errorMessage}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">
            Nothing is sent until you press Send.
          </span>
        )
      }
      footer={
        <>
          {onReview && (
            <Button
              label="Review & edit"
              variant="outline"
              size="xs"
              disabled={isValidating}
              onClick={onReview}
            />
          )}
          <Button
            label={isValidating ? "Sending…" : "Send"}
            variant="highlight"
            size="xs"
            icon={Send01}
            disabled={isValidating}
            isPulsing={isPulsing}
            onClick={onSend}
          />
        </>
      }
    >
      <p className="line-clamp-3 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
        {toPreviewText(inputs.body)}
      </p>
    </ConversationCard>
  );
}
